import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Zap, ShieldCheck, Calendar, CreditCard, Ban, CheckCircle,
  AlertCircle, RefreshCw, XCircle, ArrowUpRight
} from "lucide-react";
import { toast } from "sonner";

export default function DrishtiSubscriptionCard({ className = "" }) {
  const { user, checkAuth } = useAuth();
  const [billingInfo, setBillingInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const fetchBilling = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/payments/drishti/billing-info`, { withCredentials: true });
      setBillingInfo(res.data);
    } catch (err) {
      // Fallback to user document
      if (user?.drishti_subscription) {
        setBillingInfo({
          has_subscription: true,
          subscription: user.drishti_subscription
        });
      }
    } finally {
      setLoading(false);
    }
  }, [user?.drishti_subscription]);

  useEffect(() => {
    fetchBilling();
  }, [fetchBilling]);

  const sub = billingInfo?.subscription || user?.drishti_subscription;
  const hasAccess = Boolean(user?.has_drishti_access || billingInfo?.has_access);

  if (!hasAccess && !sub?.subscription_id && !billingInfo?.has_subscription) {
    return null; // Only render if user has an active or past subscription/access
  }

  const isMandateCancelled = sub?.status === "cancelled" || sub?.auto_debit_status === "cancelled";
  const accessUntil = sub?.access_until || sub?.expires_at;

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric"
      });
    } catch {
      return dateStr;
    }
  };

  const handleCancelAutoPay = async () => {
    setCancelling(true);
    try {
      const res = await axios.post(`${API}/payments/drishti/cancel-subscription`, {}, { withCredentials: true });
      toast.success(res.data?.message || "AutoPay mandate cancelled. Access remains valid for remaining days.");
      setConfirmModalOpen(false);
      await fetchBilling();
      if (checkAuth) await checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel AutoPay. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <>
      <Card className={`overflow-hidden border border-emerald-500/30 bg-gradient-to-br from-[#0B0F19] to-[#070A10] shadow-xl text-slate-100 ${className}`}>
        {/* Top Glow Accent */}
        <div className="h-1 w-full bg-gradient-to-r from-emerald-500 via-[#00FF88] to-cyan-500" />

        <CardHeader className="pb-3 flex flex-row items-start justify-between gap-2 flex-wrap">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-[#00FF88]/15 text-[#00FF88] flex items-center justify-center">
                <Zap className="w-4 h-4 fill-current" />
              </div>
              <CardTitle className="text-base sm:text-lg font-bold font-outfit text-white">
                Drishti AI Pro Subscription
              </CardTitle>
            </div>
            <p className="text-xs text-zinc-400 font-plex">
              3 Months Free Access Pass · Quantitative Terminal
            </p>
          </div>

          <div className="flex items-center gap-2">
            {isMandateCancelled ? (
              <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[10px] font-mono">
                Mandate Cancelled (Access Active)
              </Badge>
            ) : (
              <Badge className="bg-[#00FF88]/15 text-[#00FF88] border border-[#00FF88]/30 text-[10px] font-mono flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00FF88] animate-pulse" />
                Active AutoPay
              </Badge>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-4 pt-1">
          {/* Key Metric Blocks */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
              <span className="text-[10px] uppercase font-mono text-zinc-400 block">Setup Fee Paid</span>
              <p className="text-base font-bold font-mono text-[#00FF88] mt-0.5">₹1.00</p>
              <span className="text-[10px] text-zinc-500 font-mono">Captured (Non-refundable)</span>
            </div>

            <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
              <span className="text-[10px] uppercase font-mono text-zinc-400 block">Access Valid Until</span>
              <p className="text-sm font-bold font-mono text-white mt-0.5 truncate">
                {formatDate(accessUntil)}
              </p>
              <span className="text-[10px] text-emerald-400 font-mono">90-Day Full Access</span>
            </div>

            <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
              <span className="text-[10px] uppercase font-mono text-zinc-400 block">Next Auto-Debit</span>
              {isMandateCancelled ? (
                <>
                  <p className="text-sm font-bold font-mono text-amber-400 mt-0.5">None</p>
                  <span className="text-[10px] text-zinc-500 font-mono">No recurring charges</span>
                </>
              ) : (
                <>
                  <p className="text-sm font-bold font-mono text-amber-400 mt-0.5">
                    ₹1,000 / month
                  </p>
                  <span className="text-[10px] text-zinc-400 font-mono">
                    Starts {formatDate(sub?.first_debit_date || accessUntil)}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Instrument & Mandate Details */}
          <div className="p-3 rounded-xl bg-black/40 border border-white/[0.05] text-xs font-mono space-y-2">
            <div className="flex justify-between items-center text-zinc-400">
              <span>Payment Instrument:</span>
              <span className="text-zinc-200 font-bold">{sub?.masked_instrument || sub?.masked_payment_instrument || "UPI AutoPay"}</span>
            </div>
            {sub?.subscription_id && (
              <div className="flex justify-between items-center text-zinc-400">
                <span>Razorpay Sub ID:</span>
                <span className="text-zinc-300 font-mono select-all text-[11px]">{sub.subscription_id}</span>
              </div>
            )}
          </div>

          {/* Action Row */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 border-t border-white/[0.06]">
            <p className="text-[11px] text-zinc-400 leading-relaxed text-center sm:text-left">
              {isMandateCancelled ? (
                <span>AutoPay is cancelled. You will never be billed, and your access remains valid until {formatDate(accessUntil)}.</span>
              ) : (
                <span>Cancel your AutoPay mandate anytime with zero penalties. Your remaining days of free access remain 100% active.</span>
              )}
            </p>

            {!isMandateCancelled && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmModalOpen(true)}
                disabled={cancelling}
                className="shrink-0 h-9 px-4 text-xs font-bold text-rose-400 border-rose-500/30 hover:bg-rose-500/10 hover:text-rose-300 rounded-xl transition-all"
                data-testid="cancel-autopay-btn"
              >
                <Ban className="w-3.5 h-3.5 mr-1.5" />
                Cancel AutoPay
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Modal */}
      {confirmModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="w-full max-w-md rounded-2xl bg-[#0C101A] border border-white/10 p-6 space-y-4 shadow-2xl text-slate-100 animate-in fade-in zoom-in-95">
            <div className="w-12 h-12 rounded-2xl bg-rose-500/15 text-rose-400 flex items-center justify-center mx-auto">
              <Ban className="w-6 h-6" />
            </div>

            <div className="text-center space-y-2">
              <h3 className="text-lg font-bold text-white font-outfit">
                Cancel Drishti AutoPay Mandate?
              </h3>
              <p className="text-xs text-zinc-400 leading-relaxed font-plex">
                Are you sure you want to cancel the ₹1,000/month recurring AutoPay?
              </p>
            </div>

            <div className="p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-300 space-y-1 font-plex">
              <p className="font-bold flex items-center gap-1.5 text-emerald-400">
                <CheckCircle className="w-4 h-4" /> 100% Risk-Free Guarantee
              </p>
              <p className="text-[11px] text-zinc-300">
                Your remaining 90-day institutional access will <span className="font-bold text-white">NOT be cancelled</span>. You will continue to receive live AI signals until <span className="font-bold text-white">{formatDate(accessUntil)}</span> with zero further charges.
              </p>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => setConfirmModalOpen(false)}
                disabled={cancelling}
                className="flex-1 h-10 text-xs font-bold rounded-xl text-zinc-400 hover:text-white"
              >
                Keep AutoPay
              </Button>
              <Button
                onClick={handleCancelAutoPay}
                disabled={cancelling}
                className="flex-1 h-10 text-xs font-bold rounded-xl bg-rose-600 hover:bg-rose-700 text-white shadow-lg"
                data-testid="confirm-cancel-autopay-btn"
              >
                {cancelling ? (
                  <span className="flex items-center gap-1.5">
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Cancelling...
                  </span>
                ) : (
                  "Yes, Cancel Mandate"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
